import { BrowserModule } from "@angular/platform-browser";
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { CoreModule } from "./core/core.module";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { ToastrModule } from "ngx-toastr";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
} from "@angular/common/http";

import { AppComponent } from "./app.component";
import { OtpComponent } from "./pages/otp/otp/otp.component";
import { PaymentComponent } from "./pages/payment/payment/payment.component";
import { IswErrorPipe } from "./pages/shared/pipes/isw-error.pipe";
import { RequestInterptor } from "./core/helpers/request-interptor";
import { PaymentService } from "./pages/shared/services/payment.service";
import { HttpHeaderService } from "./core/helpers/http-headers";
import { UssdComponent } from "./pages/USSD/ussd/ussd.component";
import { SuccessComponent } from "./pages/shared/components/success/success.component";
import { BankComponent } from "./pages/bank/bank.component";
import { HeaderComponent } from "./pages/shared/components/header/header.component";
import { PageLayoutComponent } from "./pages/page-layout/page-layout.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { DetailsCaptureComponent } from "./pages/details-capture/details-capture.component";
import { MOMOComponent } from "./pages/momo/momo.component";
import { MPGSComponent } from "./pages/mpgs/mpgs.component";
import { MastercardAuthPageComponent } from "./pages/shared/components/mastercard-auth-page/mastercard-auth-page.component";
import { CardProcessingModule } from "./pages/card-processing/card-processing.module";
import { FinalTransactionStatusComponent } from "./pages/shared/components/final-transaction-status/final-transaction-status.component";
import { ExperienceRatingComponent } from "./pages/shared/components/experience-rating/experience-rating.component";

import { QrCodeComponent } from "./pages/qr-code/qr-code.component";
import { ErrorHandlingInterceptorService } from "./core/helpers/error-handling-interceptor.service";
import { NumbersOnlyDirective } from "./pages/card-processing/numbers-only.directive";
import { LoadingInterceptorService } from "./core/loading-interceptor.service";
import { NotFoundComponent } from "./pages/not-found/not-found.component";
import { RecurringPaymentComponent } from "./pages/recurring-payment/recurring-payment.component";
import { RecurringCardPaymentComponent } from "./pages/recurring-payment/components/recurring-card-payment/recurring-card-payment.component";
import { RecurringBankPaymentComponent } from "./pages/recurring-payment/components/recurring-bank-payment/recurring-bank-payment.component";
import { RecurringDetailsComponent } from "./pages/recurring-payment/components/recurring-details/recurring-details.component";
import { RecurringDetailsContainerComponent } from "./pages/recurring-payment/components/recurring-details-container/recurring-details-container.component";
import { RecurringPaymentSuccessComponent } from "./pages/recurring-payment/components/recurring-payment-success/recurring-payment-success.component";
import { RecurringPaymentDeclinedComponent } from "./pages/recurring-payment/components/recurring-payment-declined/recurring-payment-declined.component";
import { ReoccuringOtpComponent } from "./pages/recurring-payment/components/reoccuring-otp/reoccuring-otp.component";
import { InvoicePaymentComponent } from "./pages/invoicing/components/invoice-payment/invoice-payment.component";
import { OpayComponent } from "./pages/opay/opay.component";
import { OpaySuccessComponent } from "./pages/shared/components/opay-success/opay-success.component";
import { BanksComponent } from "./pages/banks/banks.component";
import { ErrorDialogComponent } from "src/app/core/error-dialog/error-dialog.component";
import { FooterComponent } from "src/app/core/footer/footer.component";
import { CollectionComponent } from "./pages/collections/collection/collection.component";
import { CollectionDetailsComponent } from "./pages/collections/collection-details/collection-details.component";
import { ReceiptComponent } from "./pages/collections/receipt/receipt.component";

const appRoutes: Routes = [
  {
    path: "payment/collection",
    component: CollectionComponent,
    pathMatch: "full",
  },
  {
    path: "payment/receipt",
    component: ReceiptComponent,
    pathMatch: "full",
  },
  {
    path: "payment/collection/details",
    component: CollectionDetailsComponent,
  },
  { path: "rating", component: ExperienceRatingComponent },
  {
    path: "subscribe/:ref/:clientId",
    component: RecurringPaymentComponent,
  },
  {
    path: "instalment/:ref/:clientId",
    component: RecurringPaymentComponent,
  },
  {
    path: "client-details/:clientId",
    component: RecurringDetailsComponent,
  },

  {
    path: "subscriptions/success",
    component: RecurringPaymentSuccessComponent,
  },

  {
    path: "invoice/:code",
    component: InvoicePaymentComponent,
  },
  { path: "pay/:payRef", component: PageLayoutComponent },
  { path: "gh/pay/:payRef", component: PageLayoutComponent },
  { path: ":page", component: DetailsCaptureComponent },
  { path: "gh/:page", component: DetailsCaptureComponent },
  { path: "mpgs/:id", component: MastercardAuthPageComponent },
  {
    // path: "opay/:txnRef/:amount/:status",
    path: "banks/:data",
    loadComponent: () =>
      import("./pages/shared/components/opay-success/opay-success.component").then(
        (m) => m.OpaySuccessComponent,
      ),
  },
  {
    path: "checkout/success/:amount/:status/:callback",
    component: SuccessComponent,
  },

  { path: "status/:statusObj", component: FinalTransactionStatusComponent },
  {
    path: "payment/completed",
    component: FinalTransactionStatusComponent,
    pathMatch: "full",
  },
  { path: "failure/:amount/:ref/:callback", component: SuccessComponent },
  { path: "bank/:client/:reference", component: PageLayoutComponent },
  { path: "header", component: HeaderComponent },
  {
    path: "payments/:clientId",
    component: RecurringDetailsContainerComponent,
  },
  {
    path: "payments/declined",
    component: RecurringPaymentDeclinedComponent,
  },
];

@NgModule({
  declarations: [
    AppComponent,
    OtpComponent,
    PaymentComponent,
    IswErrorPipe,
    UssdComponent,
    SuccessComponent,
    BankComponent,
    HeaderComponent,
    PageLayoutComponent,
    DetailsCaptureComponent,
    MOMOComponent,
    MPGSComponent,
    MastercardAuthPageComponent,
    FinalTransactionStatusComponent,
    ExperienceRatingComponent,
    QrCodeComponent,
    NumbersOnlyDirective,
    NotFoundComponent,
    RecurringPaymentComponent,
    RecurringCardPaymentComponent,
    RecurringBankPaymentComponent,
    RecurringDetailsComponent,
    RecurringDetailsContainerComponent,
    RecurringPaymentSuccessComponent,
    RecurringPaymentDeclinedComponent,
    ReoccuringOtpComponent,
    InvoicePaymentComponent,
    OpayComponent,
    BanksComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    RouterModule.forRoot(
      appRoutes,
      // ,{ enableTracing: true } // <-- debugging purposes only
    ),
    CoreModule,
    FormsModule,
    ReactiveFormsModule,
    CardProcessingModule,
    NgbModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    ErrorDialogComponent,
    FooterComponent,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: RequestInterptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlingInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptorService,
      multi: true,
    },
    PaymentService,
    HttpHeaderService,
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    {
      provide: "nonce",
      useValue: "cfb66dc91c74af335a73f10a4c8041e7",
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
